import { useTranslation } from "react-i18next";
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { Step } from "react-joyride";
import HelloDuck from "assets/helloDuck.png";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const sidebarStepTextArr = [
  "onboardingSidebarText1",
  "onboardingSidebarText2",
  "onboardingSidebarText3",
  "onboardingSidebarText4",
];

const headerBarStepTextArr = [
  "onboardingProfileText1",
  "onboardingProfileText2",
  "onboardingProfileText3",
  "onboardingProfileText4",
];

export const useMobileOnboardingSteps = (): Step[] => {
  const { t } = useTranslation();
  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: t("onboardingBodyTitle"),
      locale: {
        next: t("letsGo"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <Box sx={{ display: "flex" }}>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingBodyDescription")}
          </Typography>
          <Box
            sx={{
              flex: "0 0 40%",
              margin: "0 auto",
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${HelloDuck})`,
            }}
          />
        </Box>
      ),
    },
    {
      target: '[data-tour="profile-info"]',
      placement: "bottom",
      title: t("onboardingProfileTitle"),
      disableBeacon: true,
      spotlightPadding: 12,
      locale: {
        next: t("next"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingProfileSubtitle")}
          </Typography>
          <List sx={{ "&.MuiList-root": { padding: "4px 0px 0px 0px" } }}>
            {headerBarStepTextArr.map((text, index) => (
              <ListItem key={index} disableGutters disablePadding sx={{ alignItems: "flex-start" }}>
                <ListItemIcon
                  sx={{
                    marginTop: "4px",
                    minWidth: 20,
                    color: "primary.main",
                  }}
                >
                  <FiberManualRecordIcon sx={{ width: "14px", height: "14px" }} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(text)}
                  sx={{ "&.MuiListItemText-root": { margin: 0 }, "& .MuiTypography-root": { fontSize: "14px" } }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    },
    {
      target: '[data-tour="mobile-sidebar-open-button"]',
      title: t("onboardingMobileMenuTitle"),
      disableBeacon: true,
      spotlightPadding: 0,
      placement: "bottom",
      locale: {
        next: t("next"),
        back: t("back"),
        skip: "SKIP",
      },
      content: t("onboardingMobileMenuDescription"),
    },
    {
      target: '[data-tour="sidebar"]',
      title: t("onboardingSidebarTitle"),
      placement: "bottom",
      disableBeacon: true,
      spotlightPadding: 0,
      locale: {
        next: t("next"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingSidebarMobileDescription")}
          </Typography>
        </>
      ),
    },
    {
      target: '[data-tour="mobile-create-button"]',
      title: t("onboardingCreatePwaButtonTitle"),
      disableBeacon: true,
      placement: "bottom",
      locale: { back: t("back"), last: t("close") },
      content: t("onboardingCreatePwaButtonDescription"),
    },
  ];
};

export const useDesktopOnboardingSteps = (): Step[] => {
  const { t } = useTranslation();

  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: t("onboardingBodyTitle"),
      locale: {
        next: t("letsGo"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <Box sx={{ display: "flex" }}>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingBodyDescription")}
          </Typography>
          <Box
            sx={{
              flex: "0 0 40%",
              margin: "0 auto",
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${HelloDuck})`,
            }}
          />
        </Box>
      ),
    },
    {
      target: '[data-tour="sidebar"]',
      title: t("onboardingSidebarTitle"),
      placement: "right",
      disableBeacon: true,
      locale: {
        next: t("next"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingSidebarSubtitle")}
          </Typography>
          <List sx={{ "&.MuiList-root": { padding: "4px 0px 0px 0px" } }}>
            {sidebarStepTextArr.map((text, index) => (
              <ListItem key={index} disableGutters disablePadding sx={{ alignItems: "flex-start" }}>
                <ListItemIcon
                  sx={{
                    marginTop: "4px",
                    minWidth: 20,
                    color: "primary.main",
                  }}
                >
                  <FiberManualRecordIcon sx={{ width: "14px", height: "14px" }} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(text)}
                  sx={{ "&.MuiListItemText-root": { margin: 0 }, "& .MuiTypography-root": { fontSize: "14px" } }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    },
    {
      target: '[data-tour="profile-info"]',
      title: t("onboardingProfileTitle"),
      disableBeacon: true,
      locale: {
        next: t("next"),
        back: t("back"),
        skip: "SKIP",
      },
      content: (
        <>
          <Typography
            sx={{
              color: "#2E263D",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontSize: "14px",
              lineHeight: 1.38,
              textAlign: "left",
            }}
          >
            {t("onboardingProfileSubtitle")}
          </Typography>
          <List sx={{ "&.MuiList-root": { padding: "4px 0px 0px 0px" } }}>
            {headerBarStepTextArr.map((text, index) => (
              <ListItem key={index} disableGutters disablePadding sx={{ alignItems: "flex-start" }}>
                <ListItemIcon
                  sx={{
                    marginTop: "4px",
                    minWidth: 20,
                    color: "primary.main",
                  }}
                >
                  <FiberManualRecordIcon sx={{ width: "14px", height: "14px" }} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(text)}
                  sx={{ "&.MuiListItemText-root": { margin: 0 }, "& .MuiTypography-root": { fontSize: "14px" } }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    },
    {
      target: '[data-tour="create-button"]',
      title: t("onboardingCreatePwaButtonTitle"),
      disableBeacon: true,
      placement: "top",
      locale: { back: t("back"), last: t("close") },
      content: t("onboardingCreatePwaButtonDescription"),
    },
  ];
};
